import { useAtomValue } from 'jotai';

import useAuth from '@/hooks/useAuth';

import { useIsReportsRoute } from '@/utils/url';

import Constants from '@/constants';

import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

export interface UseLeaderOrLicenseeLocationsOptions {
  /**
   * When true, returns all licensee locations instead of the selected one.
   * Used for licensee-specific views that need all locations.
   * @default false
   */
  isLicenseeMode?: boolean;
}

/**
 * Whether licensee staff should use the Reports picker (vs permissioned locations).
 * True on `/reports`; false on `/plans` and elsewhere.
 */
export const useLicenseeStaffUsesReportsPicker = (): boolean => {
  const { data: auth } = useAuth();
  const isReportsRoute = useIsReportsRoute();
  return (auth?.userIsLicenseeStaff ?? false) && isReportsRoute;
};

/**
 * Hook to get user-level locations for API queries.
 *
 * For non-licensee users: Returns all locations with at least leader permissions.
 * For licensee users (not staff): Returns the selected location from the picker.
 * For licensee staff on Reports: Returns the Reports picker selection.
 * For licensee staff on Plans: Returns permissioned trainer locations (not the picker).
 * For licensee mode: Returns all licensee locations.
 *
 * @example
 * ```tsx
 * const userLocations = useLeaderOrLicenseeLocations();
 *
 * const { data } = useQuery({
 *   ...getPlansOptions({ query: { locationNumbers: userLocations } }),
 *   enabled: userLocations.length > 0,
 * });
 * ```
 */
export const useLeaderOrLicenseeLocations = ({
  isLicenseeMode = false,
}: UseLeaderOrLicenseeLocationsOptions = {}): string[] => {
  const { data: auth } = useAuth();
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const isReportsRoute = useIsReportsRoute();

  const isLicenseeUser = auth?.isLicensee ?? false;
  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;
  const userLicenseeLocations = auth?.licenseeLocations ?? [];
  const locationsWithAtLeastLeaderRoles =
    auth?.allLocationsWithAtLeastLeaderRoles ?? [];
  const allLocationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  let result: string[] = [];
  let context = '';

  if (isLicenseeMode) {
    context = 'licenseeMode';
    result = userLicenseeLocations
      .map(location => location.number)
      .filter(
        (num): num is string =>
          num !== undefined &&
          num !== '' &&
          num !== Constants.SUPPORT_STAFF_LOCATION,
      );
  } else if (userIsLicenseeStaff) {
    if (isReportsRoute) {
      context = 'licenseeStaffReports';
      const locationNumber = activeLicenseeLocation?.number;
      if (
        locationNumber &&
        locationNumber !== Constants.SUPPORT_STAFF_LOCATION
      ) {
        result = [locationNumber];
      }
    } else {
      context = 'licenseeStaffPlans';
      result = allLocationsWithAtLeastTrainer.filter(
        loc => loc !== Constants.SUPPORT_STAFF_LOCATION,
      );
    }
  } else if (isLicenseeUser) {
    context = 'licenseeUser';
    const locationNumber = activeLicenseeLocation?.number;
    if (locationNumber && locationNumber !== Constants.SUPPORT_STAFF_LOCATION) {
      result = [locationNumber];
    }
  } else {
    result = locationsWithAtLeastLeaderRoles;
  }

  if (result.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(
        `[useLeaderOrLicenseeLocations] Falling back to locationsWithAtLeastLeaderRoles`,
        {
          context,
          userId: auth?.userId,
          activeLicenseeLocation: activeLicenseeLocation?.number,
          userLicenseeLocationsCount: userLicenseeLocations.length,
          locationsWithAtLeastLeaderRoles,
        },
      );
    }
    return locationsWithAtLeastLeaderRoles;
  }

  return result;
};

export default useLeaderOrLicenseeLocations;
